import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Tag, Tooltip, Typography, message as antdMessage } from "antd";
import {
    AlertTriangle,
    Bot,
    CheckCircle2,
    ChevronDown,
    Copy,
    Lightbulb,
    RefreshCw,
    Sparkles,
    Zap,
} from "lucide-react";
import { explainFeedback } from "../../Services/feedback_explainer.service";
import "./FeedbackExplainPanel.css";

const { Text, Paragraph } = Typography;

const CONFIDENCE_CONFIG = {
    high:   { color: "#1f9d55", icon: CheckCircle2,  label: "Haute confiance" },
    medium: { color: "#f5a524", icon: AlertTriangle, label: "Confiance moyenne" },
    low:    { color: "#d4373a", icon: AlertTriangle, label: "Confiance faible" },
};

const COMPONENT_LABELS = {
    backend: "Backend",
    frontend: "Frontend",
    llm: "Modèle IA",
    infrastructure: "Infrastructure",
    unknown: "Non déterminé",
};

/**
 * Panneau inline de diagnostic IA — remplace la modale (anti-pattern page→drawer→modal).
 * S'intègre directement dans le drawer de détail feedback.
 *
 * - Fermé par défaut : CTA "Expliquer" pleine largeur, premium.
 * - Cliqué : auto-charge, déroule avec animation, met les actions en tête de panneau.
 * - Stocke en mémoire le diagnostic par feedbackId (évite refetch au re-toggle).
 */
const FeedbackExplainPanel = ({ feedbackId }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [explanation, setExplanation] = useState(null);
    const [error, setError] = useState(null);
    const cacheRef = useRef(new Map()); // feedbackId -> explanation

    // Reset à chaque changement de feedback sélectionné
    useEffect(() => {
        setOpen(false);
        setError(null);
        setExplanation(cacheRef.current.get(feedbackId) || null);
    }, [feedbackId]);

    const loadExplanation = useCallback(async (forceRefresh = false) => {
        if (!feedbackId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await explainFeedback(feedbackId, { forceRefresh });
            setExplanation(data);
            cacheRef.current.set(feedbackId, data);
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "Échec de l'analyse IA";
            setError(detail);
            antdMessage.error(detail);
        } finally {
            setLoading(false);
        }
    }, [feedbackId]);

    const handleToggle = () => {
        if (!open) {
            setOpen(true);
            if (!explanation && !loading) loadExplanation(false);
        } else {
            setOpen(false);
        }
    };

    const handleCopy = (event) => {
        event.stopPropagation();
        if (!explanation) return;
        const text = `# Diagnostic IA – Feedback ${feedbackId}

**Résumé:** ${explanation.summary}

**Cause racine:**
${explanation.root_cause}

**Confiance:** ${explanation.confidence}

**Preuves:**
${(explanation.evidence || []).map((e, i) => `${i + 1}. ${e}`).join("\n")}

**Correctifs suggérés:**
${(explanation.suggested_fixes || []).map((f, i) => `${i + 1}. ${f}`).join("\n")}

**Composant affecté:** ${COMPONENT_LABELS[explanation.affected_component] || explanation.affected_component || "—"}
**Action immédiate requise:** ${explanation.requires_immediate_action ? "Oui" : "Non"}

---
Généré par: ${explanation.llm_meta?.model || "IA"} (${explanation.llm_meta?.region || "—"})
${explanation.llm_meta?.generated_at || ""}`.trim();

        navigator.clipboard.writeText(text)
            .then(() => antdMessage.success("Diagnostic copié"))
            .catch(() => antdMessage.error("Échec de la copie"));
    };

    const handleRefresh = (event) => {
        event.stopPropagation();
        loadExplanation(true);
    };

    const confidenceConf = explanation
        ? CONFIDENCE_CONFIG[explanation.confidence] || CONFIDENCE_CONFIG.low
        : null;
    const ConfidenceIcon = confidenceConf?.icon || AlertTriangle;

    return (
        <section className={`explain-panel ${open ? "is-open" : "is-closed"}`}>
            {/* ─────────── Toggle / header ─────────── */}
            <button
                type="button"
                className="explain-panel__toggle"
                onClick={handleToggle}
                aria-expanded={open}
                aria-controls={`explain-body-${feedbackId || "x"}`}
            >
                <span className="explain-panel__toggle-icon">
                    <Sparkles size={15} strokeWidth={2.2} />
                </span>
                <span className="explain-panel__toggle-text">
                    <span className="explain-panel__toggle-title">
                        Diagnostic IA
                        {explanation && !open && (
                            <span className="explain-panel__toggle-pill">prêt</span>
                        )}
                        {loading && !open && (
                            <span className="explain-panel__toggle-pill is-loading">analyse…</span>
                        )}
                    </span>
                    <span className="explain-panel__toggle-hint">
                        {open
                            ? "Synthèse des logs et métadonnées système"
                            : explanation
                                ? "Cliquer pour afficher l'analyse"
                                : "Comprendre ce retour avec l'IA"}
                    </span>
                </span>

                {open && explanation && (
                    <span className="explain-panel__toggle-actions" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Copier le diagnostic">
                            <Button
                                size="small"
                                type="text"
                                icon={<Copy size={13} />}
                                onClick={handleCopy}
                                className="explain-panel__icon-btn"
                                aria-label="Copier"
                            />
                        </Tooltip>
                        <Tooltip title="Régénérer l'analyse">
                            <Button
                                size="small"
                                type="text"
                                icon={<RefreshCw size={13} />}
                                onClick={handleRefresh}
                                loading={loading}
                                className="explain-panel__icon-btn"
                                aria-label="Régénérer"
                            />
                        </Tooltip>
                    </span>
                )}

                <ChevronDown
                    size={16}
                    className={`explain-panel__chevron ${open ? "is-open" : ""}`}
                />
            </button>

            {/* ─────────── Body collapsable ─────────── */}
            <div
                id={`explain-body-${feedbackId || "x"}`}
                className="explain-panel__body-wrap"
                aria-hidden={!open}
            >
                <div className="explain-panel__body">
                    {error && (
                        <Alert
                            message="Erreur d'analyse"
                            description={error}
                            type="error"
                            showIcon
                            className="explain-panel__error"
                            action={
                                <Button size="small" onClick={() => loadExplanation(true)}>
                                    Réessayer
                                </Button>
                            }
                        />
                    )}

                    {loading && !explanation && (
                        <div className="explain-panel__loading">
                            <Sparkles size={26} className="explain-panel__loading-icon" />
                            <Text className="explain-panel__loading-text">
                                L'IA analyse les logs et le contexte…
                            </Text>
                            <Text className="explain-panel__loading-hint">
                                Cela peut prendre jusqu'à 30 secondes
                            </Text>
                        </div>
                    )}

                    {explanation && (
                        <div className="explain-panel__content">
                            <section className="explain-panel__hero">
                                <div className="explain-panel__hero-label">
                                    <Zap size={13} />
                                    Résumé utile
                                </div>
                                <Paragraph className="explain-panel__summary">
                                    {explanation.summary}
                                </Paragraph>
                                <div className="explain-panel__chips">
                                    {confidenceConf && (
                                        <Tag
                                            className="explain-panel__chip explain-panel__chip--confidence"
                                            style={{
                                                color: confidenceConf.color,
                                                borderColor: confidenceConf.color,
                                                background: `${confidenceConf.color}14`,
                                            }}
                                        >
                                            <ConfidenceIcon size={11} strokeWidth={2.6} />
                                            {confidenceConf.label}
                                        </Tag>
                                    )}
                                    <Tag className="explain-panel__chip explain-panel__chip--soft">
                                        {COMPONENT_LABELS[explanation.affected_component]
                                            || explanation.affected_component
                                            || "Composant inconnu"}
                                    </Tag>
                                    <Tag className={`explain-panel__chip ${explanation.requires_immediate_action ? "explain-panel__chip--urgent" : "explain-panel__chip--soft"}`}>
                                        {explanation.requires_immediate_action ? "Action requise" : "Pas d'urgence"}
                                    </Tag>
                                </div>
                            </section>

                            <section className="explain-panel__section">
                                <div className="explain-panel__section-head">
                                    <AlertTriangle size={14} />
                                    <span>Cause probable</span>
                                </div>
                                <Paragraph className="explain-panel__text">
                                    {explanation.root_cause}
                                </Paragraph>
                            </section>

                            {explanation.evidence?.length > 0 && (
                                <section className="explain-panel__section">
                                    <div className="explain-panel__section-head">
                                        <CheckCircle2 size={14} />
                                        <span>Preuves</span>
                                    </div>
                                    <ul className="explain-panel__list">
                                        {explanation.evidence.map((item, idx) => (
                                            <li key={idx}>{item}</li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {explanation.suggested_fixes?.length > 0 && (
                                <section className="explain-panel__section">
                                    <div className="explain-panel__section-head">
                                        <Lightbulb size={14} />
                                        <span>Correctifs suggérés</span>
                                    </div>
                                    <ol className="explain-panel__list explain-panel__list--ordered">
                                        {explanation.suggested_fixes.map((fix, idx) => (
                                            <li key={idx}>{fix}</li>
                                        ))}
                                    </ol>
                                </section>
                            )}

                            {explanation.llm_meta && (
                                <div className="explain-panel__meta">
                                    <div>
                                        <span>Modèle</span>
                                        <strong>
                                            <Bot size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} />
                                            {explanation.llm_meta.model?.split("/").pop() || "—"}
                                        </strong>
                                    </div>
                                    <div>
                                        <span>Logs analysés</span>
                                        <strong>{explanation.llm_meta.logs_count || 0}</strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default FeedbackExplainPanel;

